import PropTypes from 'prop-types';

import { useDispatch } from 'react-redux';

import { actionCreators as modalActions } from 'react/api/actions/modals';
import { generateLinkToDownloadCSV, getResourceTo } from 'react/utils/utils';

function useIntegrations(nameSpace) {
  const dispatch = useDispatch();

  const setRequestDateRangeFilters = (filter, activeFilters) => {
    const dateTimeUtils = Monet.Format.Date;
    const filterValue = filter.getValue();
    const { ini: startDate, fin: endDate } = filterValue;

    if (startDate) {
      const formattedDate = dateTimeUtils.format(
        startDate,
        {
          locale: 'en',
          pattern: 'YYYY-MM-DDTHH:mm:ss'
        }
      );
      activeFilters.startDate = formattedDate;
    }

    if (endDate) {
      const formattedDate = dateTimeUtils.format(
        endDate,
        {
          locale: 'en',
          pattern: 'YYYY-MM-DDTHH:mm:ss'
        }
      );
      activeFilters.endDate = formattedDate;
    }
  };

  const getActiveFilters = () => {
    const fields = App.Forms[nameSpace]?.filterBuilder?.fields || {};
    const activeFilters = {};

    Object.keys(fields).forEach((key) => {
      const filter = fields[key];

      switch (filter.name) {
        case 'requestDate_Range': {
          setRequestDateRangeFilters(filter, activeFilters);

          break;
        }
        case 'organization': {
          const { model: { description: organizationValue } } = filter.getFullValue();

          if (organizationValue && String(organizationValue)?.length) {
            activeFilters[key] = organizationValue;
          }

          break;
        }
        default: {
          const filterValue = filter.getValue();

          if (filterValue && String(filterValue)?.length) {
            activeFilters[key] = filterValue;
          }

          break;
        }
      }
    });

    return activeFilters;
  };

  const downloadCSVFromData = ({
    fields,
    formFields,
    fileName,
    exportFormats
  }) => {
    const parsedFields = fields
        .map((field) => field.replace(/"/g, ''))
        .map((field) => (field === 'organization.description' ? 'organization' : field));

    let data = '';

    data += Object.values(parsedFields)
      .map((field) => {
        const fieldData = formFields[field];
        return fieldData?.translatedLabel || getResourceTo(fieldData?.label);
      })
      .join(',');

    data += '\n';

    const formattedData = App.Forms[nameSpace].dataProvider.data
      .map((item) => {
        const itemModel = item.model.attributes;

        return parsedFields
          .map((field) => {
            const value = itemModel[field];

            if (value === null) {
              return '';
            }

            if (['requestBody', 'responseBody'].includes(field)) {
              return JSON.stringify(value).replace(/,/g, '$');
            }

            return String(value).replace(/,/g, '.');
          })
          .join(',');
      })
      .join('\n');

    data += formattedData;

    generateLinkToDownloadCSV({
      fileName,
      data,
      callback: () => {
        exportFormats.inProgress = false;
        exportFormats.handlerToggleLoadingInBtn();
        exportFormats.togglePopUp();
      }
    });
  };

  const setSortBy = () => {
    App.Forms[nameSpace].table.sortBy = 'client';
  };

  const setCustomColumnsFormatter = () => {
    const tableColumns = App.Forms[nameSpace]?.table?.getColumns() || [];

    const updatedTableColums = tableColumns.map((columnItem) => {
      switch (columnItem.field) {
        case 'httpStatus':
          return {
            ...columnItem,
            width: 86,
            formatter: (row, cell, value, column, dataContext) => `
              <div class='httpStatus-${value === 200 ? 'success' : 'error'}'></div>
            `
          };
        case 'organization.description': {
          return {
            ...columnItem,
            formatter: (row, cell, value, column, dataContext) => {
              const { model: { attributes } } = dataContext;

              return attributes.organization;
            }
          };
        }
        default:
          return columnItem;
      }
    });

    updatedTableColums.length && App.Forms[nameSpace].table.setColumns(updatedTableColums);
  };

  const setCustomOnClickRow = () => {
    const grid = App.Forms[nameSpace].table;

    const onClickRow = (e, args) => {
      const item = args.grid.getData().data[args.row];

      if (!item.model.attributes) return;

      const {
        url,
        responseBody,
        requestType,
        requestDate,
        httpStatus,
        requestBody,
        description,
        organization,
        message
      } = item.model.attributes;

      dispatch(modalActions.openModal({
        title: 'IntegrationLogModal.Title',
        modalKey: 'logDetails',
        data: {
          url,
          date: Monet.Format.Date.formatToLocale(requestDate),
          httpStatus,
          requestBody,
          requestType,
          responseMessage: message,
          responseBody,
          entityDescription: description,
          organization,
        },
      }));
    };

    grid.onClick.unsubscribe(onClickRow);
    grid.onClick.subscribe(onClickRow);
  };

  return {
    getActiveFilters,
    downloadCSVFromData,
    setSortBy,
    setCustomColumnsFormatter,
    setCustomOnClickRow
  }
}

useIntegrations.propTypes = {
  nameSpace: PropTypes.string.isRequired
};

export {
  useIntegrations
};
